import * as uuid from 'uuid';

import { type CreateLogbookRecordInput } from '../../API';
import { type ContactInput } from '../../APIExtensions';
import { toMap } from '../../utils/arrayUtils';
import { getContactByCrewId } from '../Contacts/ContactsDatasource';
import { isLegFinished } from '../FlightLegs/FlightLeg';
import { type CompleteFlight } from '../Flights/FlightsDatasource';
import { type WithId } from '../LocalDB/types';
import { getLogbookRecordsByLegId, putLogbookRecord } from '../LogbookRecords/LogbookRecordsDatasource';
import { inLegLogbookToRecord } from './InLegLogbook';

// Never change: record ids derived from it must stay the same for an already finished leg.
const LOGBOOK_RECORD_ID_NAMESPACE = '3afee172-f64f-4f97-8b0d-37f016042928';

// One record per leg and contact, so finishing a flight again (or on another device) reuses the id
// instead of creating a duplicate record.
export const getLogbookRecordId = (legId: string, contactId: string) =>
    uuid.v5(`${legId}/${contactId}`, LOGBOOK_RECORD_ID_NAMESPACE);

export async function saveFlightLogbookRecords(flight: CompleteFlight): Promise<void> {
    const getContactCache = new Map<string, Promise<ContactInput | null>>();
    const getCachedContact = (crewId: string): Promise<ContactInput | null> => {
        let maybePromise = getContactCache.get(crewId);
        if (!maybePromise) {
            maybePromise = getContactByCrewId(crewId);
            getContactCache.set(crewId, maybePromise);
        }
        return maybePromise;
    };
    await Promise.all(
        flight.legs.map(async (leg) => {
            if (!isLegFinished(leg)) {
                return;
            }
            if (!leg.logbookFields) {
                return;
            }
            const existingLogbookRecords = await getLogbookRecordsByLegId(leg.id);
            const existingRecordContactIds = new Set(existingLogbookRecords.map((lr) => lr.contactId));
            const crewIds = [leg.picId, leg.sicId].filter(Boolean) as string[];
            const contacts = (await Promise.all(crewIds.map(getCachedContact))).filter(Boolean) as ContactInput[];
            const contactIds = contacts.map((c) => c.id);
            const userIds = contacts.map((c) => c.userId).filter(Boolean) as string[];
            const contactsMap = toMap(contacts);
            const promises: Array<Promise<void>> = [];
            for (const contactId of contactIds) {
                if (existingRecordContactIds.has(contactId)) {
                    continue;
                }
                const crewId = contactsMap.get(contactId)?.crewId;
                if (!crewId) {
                    continue;
                }
                const record: CreateLogbookRecordInput & WithId = {
                    ...inLegLogbookToRecord(leg.logbookFields, leg),
                    id: getLogbookRecordId(leg.id, contactId),
                    contactId,
                    crewId,
                    editableByUserIds: userIds,
                };
                promises.push(putLogbookRecord(record));
            }
            await Promise.all(promises);
        })
    );
}
